/**
 * ManifestoSection Component
 * 
 * A compelling message/vision section that ends with a CTA.
 * Positioned after the feature showcase panels.
 */

import React from 'react';
import { motion } from 'framer-motion';
import './ManifestoSection.css';

const ManifestoSection = () => {
  // Smooth scroll to early access section
  const scrollToEarlyAccess = () => {
    const section = document.getElementById('early-access');
    if (section) {
      section.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: 'easeOut' },
    },
  };

  return (
    <section id="mission-statement" className="manifesto-section">
      <motion.div
        className="manifesto-section__container"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: '-100px' }}
        variants={containerVariants}
      >
        {/* Blue eyebrow label */}
        <motion.span className="manifesto-section__eyebrow" variants={itemVariants}>
          MISSION STATEMENT
        </motion.span>

        {/* Opening Statement */}
        <motion.p className="manifesto-section__opening" variants={itemVariants}>
          We believe the future of education doesn't look like a classroom, and it certainly shouldn't look like a chatbot. Rather, it should look like <em>you</em>.
        </motion.p>

        {/* Body Paragraphs */}
        <motion.div className="manifesto-section__body" variants={itemVariants}>
          <p>
            In a world flooding with automated content, the ability to make meaning—to interpret, guide and to truly connect is crucial. Your lived experience, your cultural grounding, and your unique way of explaining the world are not just "content." They are the foundations the next generation will learn from. Yet for too long, independent educators have been neglected or boxed in by tools that force you to be an administrator first and a mentor second.
          </p>
          
          <p>
            We believe the architecture of knowledge belong to the commons—democratized and distributed into the hands of the many. And for that decentralized future to remain pure, it must be anchored to what algorithms lack: empathy, mutual understanding, and shared human values.
          </p>
          
          <p>
            Project AANA exists to be a part of that vision and prepare you for the next. We are building a visual language engine, but you are the spearhead. We provide the visual structure; you provide the wisdom. Together, we aren't just teaching students; we are defining a new age of human-led, structurally sound learning.
          </p>
        </motion.div>

        {/* Closing Statement */}
        <motion.p className="manifesto-section__closing" variants={itemVariants}>
          Don't just watch the future happen. Lead it.
        </motion.p>

        {/* CTA Button */}
        <motion.div className="manifesto-section__cta" variants={itemVariants}>
          <button 
            className="manifesto-cta-button"
            onClick={scrollToEarlyAccess}
          >
            Register Interest
            <span className="manifesto-cta-arrow">→</span>
          </button>
        </motion.div>
      </motion.div>
    </section>
  );
};

export default ManifestoSection;
